import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, Grid, TextField,
  Divider, CircularProgress, Chip, FormControl, FormLabel,
  RadioGroup, FormControlLabel, Radio, Tooltip, Alert,
} from '@mui/material';
import { Print, Save, Assignment, LocalHospital, CheckCircle } from '@mui/icons-material';
import {
  printSettingsService, ipdDischargeSummaryService, authService,
} from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = {
  finalDiagnosis: '',
  icd10Code: '',
  clinicalHistory: '',
  conditionOnAdmission: '',
  courseInHospital: '',
  investigationsPerformed: '',
  proceduresDone: '',
  treatmentGiven: '',
  conditionOnDischarge: '',
  dischargeType: 'Normal Discharge',
  dischargeMedications: '',
  followUpAdvice: '',
  dietAdvice: '',
  activityAdvice: '',
  followUpDate: '',
};

const calcAge = (dob) => {
  if (!dob) return '';
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDT = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return d.toLocaleDateString('en-IN') + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const stayDays = (admDate, disDate) => {
  if (!admDate) return '';
  const from = new Date(admDate);
  const to = disDate ? new Date(disDate) : new Date();
  const diff = Math.round((to - from) / (1000 * 60 * 60 * 24));
  return diff <= 0 ? '< 1 day' : `${diff} day${diff > 1 ? 's' : ''}`;
};

const IpdDischargeSummaryPage = () => {
  const { admissionId } = useParams();
  const navigate = useNavigate();
  const user = authService.getCurrentUser();

  const [printSettings, setPrintSettings] = useState(null);
  const [admission, setAdmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [psRes, dsRes] = await Promise.all([
        printSettingsService.getSettings(),
        ipdDischargeSummaryService.get(admissionId),
      ]);
      setPrintSettings(psRes.data);
      const { admission: adm, summary, activeMeds } = dsRes.data;
      setAdmission(adm);

      if (summary) {
        setForm({
          finalDiagnosis:          summary.finalDiagnosis          || '',
          icd10Code:               summary.icd10Code               || '',
          clinicalHistory:         summary.clinicalHistory          || '',
          conditionOnAdmission:    summary.conditionOnAdmission     || '',
          courseInHospital:        summary.courseInHospital         || '',
          investigationsPerformed: summary.investigationsPerformed  || '',
          proceduresDone:          summary.proceduresDone           || '',
          treatmentGiven:          summary.treatmentGiven           || '',
          conditionOnDischarge:    summary.conditionOnDischarge     || '',
          dischargeType:           summary.dischargeType            || 'Normal Discharge',
          dischargeMedications:    summary.dischargeMedications     || '',
          followUpAdvice:          summary.followUpAdvice           || '',
          dietAdvice:              summary.dietAdvice               || '',
          activityAdvice:          summary.activityAdvice           || '',
          followUpDate:            summary.followUpDate ? summary.followUpDate.substring(0, 10) : '',
        });
      } else if (activeMeds?.length) {
        const medList = activeMeds.map(m =>
          `${m.medicineName} ${m.dose || ''} ${m.route} ${m.frequency}`.trim()
        ).join('\n');
        setForm(prev => ({
          ...prev,
          finalDiagnosis:       adm.admissionDiagnosis || '',
          icd10Code:            adm.icd10Code          || '',
          dischargeType:        adm.dischargeType       || 'Normal Discharge',
          dischargeMedications: medList,
        }));
      } else {
        setForm(prev => ({
          ...prev,
          finalDiagnosis: adm.admissionDiagnosis || '',
          icd10Code:      adm.icd10Code          || '',
          dischargeType:  adm.dischargeType       || 'Normal Discharge',
        }));
      }
    } catch (e) {
      setError('Failed to load discharge summary data.');
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSave = async (andPrint = false) => {
    setSaving(true);
    setSaved(false);
    try {
      await ipdDischargeSummaryService.save(admissionId, {
        finalDiagnosis:          form.finalDiagnosis,
        icd10Code:               form.icd10Code,
        clinicalHistory:         form.clinicalHistory,
        conditionOnAdmission:    form.conditionOnAdmission,
        courseInHospital:        form.courseInHospital,
        investigationsPerformed: form.investigationsPerformed,
        proceduresDone:          form.proceduresDone,
        treatmentGiven:          form.treatmentGiven,
        conditionOnDischarge:    form.conditionOnDischarge,
        dischargeType:           form.dischargeType,
        dischargeMedications:    form.dischargeMedications,
        followUpAdvice:          form.followUpAdvice,
        dietAdvice:              form.dietAdvice,
        activityAdvice:          form.activityAdvice,
        followUpDate:            form.followUpDate || null,
      });
      setSaved(true);
      if (andPrint) setTimeout(() => window.print(), 300);
    } catch (e) {
      setError('Failed to save discharge summary.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const patient     = admission?.patient;
  const patientAge  = calcAge(patient?.dateOfBirth);
  const doctorLabel = user ? `Dr. ${user.firstName} ${user.lastName}` : (admission?.doctor || '—');
  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;

  const hospitalName   = printSettings?.hospitalName   || printSettings?.HospitalName   || '';
  const hospitalAddr   = printSettings?.address        || printSettings?.Address        || '';
  const hospitalPhone  = printSettings?.phoneNumber    || printSettings?.PhoneNumber    || '';
  const logoUrl        = printSettings?.logoUrl        || printSettings?.LogoUrl        || null;

  return (
    <>
      <style>{`
        @media screen { #ipd-ds-print { display: none !important; } }
        @media print {
          @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; }
          #ipd-ds-screen { display: none !important; }
          #ipd-ds-print {
            display: block !important;
            position: absolute; left: 0; top: 0;
            width: 100%; padding: 0;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
            font-size: 12px;
          }
          #ipd-ds-print * { visibility: visible !important; }
          .pds-table { width: 100%; border-collapse: collapse; }
          .pds-table td, .pds-table th { border: 1px solid #1565c0; padding: 5px 8px; vertical-align: top; }
          .pds-label { color: #1565c0; font-weight: 600; white-space: nowrap; }
          .pds-header {
            background-color: #e3f2fd !important;
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
            color: #1565c0; font-weight: 700; font-size: 13px;
            text-align: center; padding: 6px 8px;
            border: 1px solid #1565c0; letter-spacing: 1px;
          }
          .pds-tall td { min-height: 80px; }
          .pds-hosp-header { text-align: center; margin-bottom: 8px; }
        }
      `}</style>

      {/* ── Screen View ─────────────────────────────────────────────────── */}
      <div id="ipd-ds-screen" className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <TopBar
          title="IPD Discharge Summary"
          onBack={() => navigate(-1)}
          noPrint
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={() => handleSave(false)}
                disabled={saving}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                {saving ? 'Saving…' : 'Save Draft'}
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={() => handleSave(true)}
                disabled={saving}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              >
                Save & Print
              </Button>
            </Box>
          }
        />

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {saved && (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              Discharge summary saved successfully.
            </Alert>
          )}

          {/* Patient & Admission Info */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Assignment sx={{ fontSize: 28, color: '#7b1fa2', mr: 1.5 }} />
              <Typography variant="h6" fontWeight={600}>Patient &amp; Admission Details</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Patient Name" value={patient ? `${patient.firstName} ${patient.lastName}` : '—'} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Age" value={patientAge || '—'} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth label="Gender" value={patient?.gender || '—'} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Phone" value={patient?.phoneNumber || '—'} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Ward / Bed" value={`${admission?.ward || '—'} / ${admission?.bed || '—'}`} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Admission Date" value={fmtDate(admission?.admissionDate)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Discharge Date" value={admission?.actualDischargeDate ? fmtDate(admission.actualDischargeDate) : '—'} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Consulting Doctor" value={admission?.doctor || doctorLabel} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Stay Duration" value={stayDays(admission?.admissionDate, admission?.actualDischargeDate)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Chip label={`Status: ${admission?.status || '—'}`}
                  color={admission?.status === 'Discharged' ? 'success' : 'warning'}
                  sx={{ mt: 1 }} />
              </Grid>
            </Grid>
          </Paper>

          {/* Diagnosis */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocalHospital sx={{ fontSize: 28, color: '#1565c0', mr: 1.5 }} />
              <Typography variant="h6" fontWeight={600}>Diagnosis</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth multiline rows={2} label="Final Diagnosis"
                  value={form.finalDiagnosis} onChange={set('finalDiagnosis')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="ICD-10 Code" placeholder="e.g. J18.9"
                  value={form.icd10Code} onChange={set('icd10Code')} />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Discharge Type</FormLabel>
                  <RadioGroup row value={form.dischargeType} onChange={set('dischargeType')}>
                    {['Normal Discharge', 'LAMA', 'Transferred', 'Expired'].map(t => (
                      <FormControlLabel key={t} value={t} control={<Radio size="small" />} label={t} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Clinical Details */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Clinical Details</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Clinical History"
                  helperText="Chief complaint and relevant history"
                  value={form.clinicalHistory} onChange={set('clinicalHistory')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Condition on Admission"
                  helperText="Examination findings at admission"
                  value={form.conditionOnAdmission} onChange={set('conditionOnAdmission')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} label="Course in Hospital"
                  helperText="Summary of the patient's clinical course during hospitalization"
                  value={form.courseInHospital} onChange={set('courseInHospital')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Investigations Performed"
                  value={form.investigationsPerformed} onChange={set('investigationsPerformed')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Procedures Done"
                  value={form.proceduresDone} onChange={set('proceduresDone')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Treatment Given"
                  value={form.treatmentGiven} onChange={set('treatmentGiven')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Condition on Discharge"
                  value={form.conditionOnDischarge} onChange={set('conditionOnDischarge')} />
              </Grid>
            </Grid>
          </Paper>

          {/* Discharge Instructions */}
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Discharge Instructions</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} label="Discharge Medications"
                  helperText="Auto-populated from active medication orders — edit as needed"
                  value={form.dischargeMedications} onChange={set('dischargeMedications')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Follow-Up Advice"
                  value={form.followUpAdvice} onChange={set('followUpAdvice')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={3} label="Diet Advice"
                  value={form.dietAdvice} onChange={set('dietAdvice')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth multiline rows={2} label="Activity Restrictions"
                  value={form.activityAdvice} onChange={set('activityAdvice')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Follow-Up Date" type="date"
                  InputLabelProps={{ shrink: true }}
                  value={form.followUpDate} onChange={set('followUpDate')} />
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </div>

      {/* ── Print View ──────────────────────────────────────────────────── */}
      <div id="ipd-ds-print">
        {/* Hospital Header */}
        {!isPrePrinted && (
          <div className="pds-hosp-header">
            {logoUrl && <img src={logoUrl} alt="logo" style={{ maxHeight: 60, marginBottom: 4 }} />}
            <div style={{ fontSize: 20, fontWeight: 700 }}>{hospitalName}</div>
            <div style={{ fontSize: 12 }}>{hospitalAddr}</div>
            {hospitalPhone && <div style={{ fontSize: 12 }}>Ph: {hospitalPhone}</div>}
            <hr style={{ borderColor: '#1565c0', margin: '6px 0' }} />
          </div>
        )}

        <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 16, letterSpacing: 2, marginBottom: 8,
          borderBottom: '2px solid #1565c0', paddingBottom: 4, color: '#1565c0' }}>
          DISCHARGE SUMMARY
        </div>

        {/* Patient Info Table */}
        <table className="pds-table" style={{ marginBottom: 8 }}>
          <tbody>
            <tr>
              <td className="pds-label">Patient Name</td>
              <td>{patient ? `${patient.firstName} ${patient.lastName}` : '—'}</td>
              <td className="pds-label">Age / Sex</td>
              <td>{patientAge ? `${patientAge} yrs / ${patient?.gender || '—'}` : '—'}</td>
            </tr>
            <tr>
              <td className="pds-label">Ward / Bed</td>
              <td>{admission?.ward || '—'} / {admission?.bed || '—'}</td>
              <td className="pds-label">Admission Date</td>
              <td>{fmtDT(admission?.admissionDate)}</td>
            </tr>
            <tr>
              <td className="pds-label">Discharge Date</td>
              <td>{admission?.actualDischargeDate ? fmtDT(admission.actualDischargeDate) : '—'}</td>
              <td className="pds-label">Stay Duration</td>
              <td>{stayDays(admission?.admissionDate, admission?.actualDischargeDate)}</td>
            </tr>
            <tr>
              <td className="pds-label">Consultant</td>
              <td>{admission?.doctor || doctorLabel}</td>
              <td className="pds-label">Discharge Type</td>
              <td>{form.dischargeType}</td>
            </tr>
            <tr>
              <td className="pds-label">Final Diagnosis</td>
              <td colSpan={2}>{form.finalDiagnosis || '—'}</td>
              <td><span className="pds-label">ICD-10: </span>{form.icd10Code || '—'}</td>
            </tr>
          </tbody>
        </table>

        {/* Clinical Sections */}
        {[
          { label: 'CLINICAL HISTORY', value: form.clinicalHistory },
          { label: 'CONDITION ON ADMISSION', value: form.conditionOnAdmission },
          { label: 'COURSE IN HOSPITAL', value: form.courseInHospital },
          { label: 'INVESTIGATIONS PERFORMED', value: form.investigationsPerformed },
          { label: 'PROCEDURES DONE', value: form.proceduresDone },
          { label: 'TREATMENT GIVEN', value: form.treatmentGiven },
          { label: 'CONDITION ON DISCHARGE', value: form.conditionOnDischarge },
          { label: 'DISCHARGE MEDICATIONS', value: form.dischargeMedications },
          { label: 'FOLLOW-UP ADVICE', value: form.followUpAdvice },
          { label: 'DIET ADVICE', value: form.dietAdvice },
          { label: 'ACTIVITY RESTRICTIONS', value: form.activityAdvice },
        ].map(({ label, value }) => value ? (
          <table key={label} className="pds-table" style={{ marginBottom: 4 }}>
            <tbody>
              <tr>
                <td className="pds-header" colSpan={2}>{label}</td>
              </tr>
              <tr className="pds-tall">
                <td style={{ whiteSpace: 'pre-wrap', minHeight: 50 }}>{value || '—'}</td>
              </tr>
            </tbody>
          </table>
        ) : null)}

        {/* Follow-Up Date */}
        {form.followUpDate && (
          <table className="pds-table" style={{ marginBottom: 4 }}>
            <tbody>
              <tr>
                <td className="pds-label" style={{ width: '30%' }}>Follow-Up Date</td>
                <td>{fmtDate(form.followUpDate)}</td>
              </tr>
            </tbody>
          </table>
        )}

        {/* Signature */}
        <div style={{ marginTop: 32, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ textAlign: 'center', minWidth: 200 }}>
            <div style={{ borderTop: '1px solid #000', paddingTop: 4 }}>
              <strong>{admission?.doctor || doctorLabel}</strong><br />
              <span style={{ fontSize: 11 }}>Consulting Physician</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IpdDischargeSummaryPage;
